import { useState, useRef, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Eye, EyeOff, Lock, User, Loader2, ShieldCheck, Smartphone, KeyRound, Copy, CheckCircle, AlertTriangle, ArrowRight } from 'lucide-react';
import client from '../../src/api/client';
import '../styles/admin-base.css';
import "../styles/login.css";
import { consumeAdminSessionNotice, getAdminSessionNoticeCopy } from '../../src/utils/adminSessionNotice';

export default function AdminLogin() {
  const navigate = useNavigate();

  // Step: 'credentials' | 'totp_setup' | 'totp_setup_verify' | 'totp_verify'
  const [step, setStep] = useState('credentials');

  // Credentials state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // TOTP setup state
  const [setupToken, setSetupToken] = useState('');
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [totpSecret, setTotpSecret] = useState('');
  const [backupCodes, setBackupCodes] = useState([]);
  const [savedCodes, setSavedCodes] = useState(false);

  // TOTP verify state
  const [totpToken, setTotpToken] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [isBackupCode, setIsBackupCode] = useState(false);

  // General state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [copiedSecret, setCopiedSecret] = useState(false);
  const [copiedCodes, setCopiedCodes] = useState(false);
  const [sessionEndNotice, setSessionEndNotice] = useState(null);

  const totpInputRef = useRef(null);

  useEffect(() => {
    if ((step === 'totp_setup_verify' || step === 'totp_verify') && totpInputRef.current) {
      totpInputRef.current.focus();
    }
  }, [step, isBackupCode]);

  useEffect(() => {
    const notice = consumeAdminSessionNotice();
    if (!notice) return;
    const copy = getAdminSessionNoticeCopy(notice.reason, notice.backendMessage);
    setSessionEndNotice(copy);
  }, []);

  // Step 1: Credential submission
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const response = await client.post('/api/admin/auth/login', { email, password });
      const data = response.data;

      if (data.requiresTotpSetup) {
        setSetupToken(data.setupToken);
        await initTotpSetup(data.setupToken);
      } else if (data.requiresTotp) {
        setTotpToken(data.totpToken);
        setStep('totp_verify');
      } else if (data.token && data.admin) {
        completeLogin(data.token, data.admin);
      }
    } catch (err) {
      const status = err.response?.status;
      const msg = err.response?.data?.message || err.message;
      if (status === 423) {
        const mins = err.response?.data?.lockedUntilMinutes;
        setError(`Account temporarily locked. Try again in ${mins || 30} minute(s).`);
      } else {
        setError(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  // Step 2a: Initialize TOTP setup (generate QR)
  const initTotpSetup = async (token) => {
    setLoading(true);
    try {
      const response = await client.post('/api/admin/auth/totp/setup', { setupToken: token });
      const data = response.data;
      setQrCodeUrl(data.qrCodeUrl);
      setTotpSecret(data.secret);
      setBackupCodes(data.backupCodes);
      setSetupToken(data.setupToken);
      setSavedCodes(false);
      setCopiedSecret(false);
      setCopiedCodes(false);
      setStep('totp_setup');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to initialize authenticator setup.');
      setStep('credentials');
    } finally {
      setLoading(false);
    }
  };

  // Step 2b: Verify TOTP setup code
  const handleVerifySetup = async (e) => {
    e.preventDefault();
    if (!totpCode.trim()) return;
    setLoading(true);
    setError(null);

    try {
      const response = await client.post('/api/admin/auth/totp/verify-setup', {
        setupToken,
        code: totpCode.trim(),
        backupCodes,
      });
      const { token, admin } = response.data;
      completeLogin(token, admin);
    } catch (err) {
      setError(err.response?.data?.message || 'Invalid verification code.');
      setTotpCode('');
    } finally {
      setLoading(false);
    }
  };

  // Step 3: Verify TOTP for returning admin
  const handleVerifyTotp = async (e) => {
    e.preventDefault();
    if (!totpCode.trim()) return;
    setLoading(true);
    setError(null);

    try {
      const response = await client.post('/api/admin/auth/totp/verify', {
        totpToken,
        code: totpCode.trim(),
        isBackupCode,
      });
      const { token, admin } = response.data;
      completeLogin(token, admin);
    } catch (err) {
      const msg = err.response?.data?.message || 'Verification failed.';
      setError(msg);
      setTotpCode('');
    } finally {
      setLoading(false);
    }
  };

  const completeLogin = (token, admin) => {
    localStorage.setItem('adminToken', token);
    localStorage.setItem('adminUser', JSON.stringify(admin));
    client.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    navigate('/admin/dashboard');
  };

  const copyToClipboard = async (text, setCopied) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleTotpCodeChange = (e) => {
    const val = e.target.value;
    if (isBackupCode) {
      if (/^[a-fA-F0-9]{0,8}$/.test(val)) {
        setTotpCode(val);
      }
    } else {
      if (/^\d{0,6}$/.test(val)) {
        setTotpCode(val);
      }
    }
  };

  const handleBackToLogin = (event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    setLoading(false);
    setIsBackupCode(false);
    setTotpCode('');
    setTotpToken('');
    setSetupToken('');
    setQrCodeUrl('');
    setTotpSecret('');
    setBackupCodes([]);
    setSavedCodes(false);
    setCopiedSecret(false);
    setCopiedCodes(false);
    setError(null);
    setStep('credentials');
  };

  // Credential step
  if (step === 'credentials') {
    return (
      <div className="admin-login-container admin-login-container--credentials">
        <div className="admin-login-shell">
          <section className="admin-login-intro" aria-label="Admin access information">
            <span className="admin-login-intro__badge">Secure Admin Access</span>
            <h1>Welcome to the Admin Access Portal</h1>
            <p>
              Sign in to manage members, requests, and administrative operations. Every login is protected with
              multi-step security controls designed for authorized personnel.
            </p>
            <ul className="admin-login-intro__highlights" aria-label="Security highlights">
              <li>
                <ShieldCheck size={18} />
                <span>Role-based access for authorized admin accounts only.</span>
              </li>
              <li>
                <Smartphone size={18} />
                <span>Authenticator verification is enforced on sign-in.</span>
              </li>
              <li>
                <KeyRound size={18} />
                <span>Backup code recovery support for account continuity.</span>
              </li>
            </ul>
          </section>

          <section className="admin-login-form-column">
            <div className="admin-login-card admin-login-card--credentials">
              <div className="admin-login-header">
                <div className="admin-logo-circle">
                  <ShieldCheck size={30} color="#1f4f85" />
                </div>
                <h2>Admin Login</h2>
                <p>Secure access for authorized personnel only</p>
              </div>

              <form onSubmit={handleLogin} className="admin-login-form">
                {error && <div className="admin-alert is-error">{error}</div>}

                <div className="admin-form-group">
                  <label htmlFor="email">Email Address</label>
                  <div className="admin-input-wrapper">
                    <User size={18} className="admin-input-icon" />
                    <input
                      type="email"
                      id="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="admin@example.com"
                      required
                      className="admin-input"
                    />
                  </div>
                </div>

                <div className="admin-form-group">
                  <div className="admin-form-header">
                    <label htmlFor="password">Password</label>
                    <Link to="/admin/forgot-password" className="admin-forgot-password">Forgot Password?</Link>
                  </div>
                  <div className="admin-input-wrapper">
                    <Lock size={18} className="admin-input-icon" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      id="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Enter your password"
                      required
                      className="admin-input"
                    />
                    <button
                      type="button"
                      className="admin-password-toggle"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                <button type="submit" className="admin-button is-primary is-full-width" disabled={loading}>
                  {loading ? (
                    <Loader2 size={18} className="admin-spin" />
                  ) : (
                    <>
                      <span>Sign In</span>
                      <ArrowRight size={18} />
                    </>
                  )}
                </button>
              </form>
            </div>
          </section>
        </div>

        <footer className="admin-login-page-footer">
          <p>Need help? Contact your union representative</p>
          <p>Labor Connect - Luzon regional</p>
        </footer>

        {sessionEndNotice ? (
          <div className="admin-dialog" role="dialog" aria-modal="true" aria-label="Admin session ended">
            <div className="admin-dialog__backdrop" onClick={() => setSessionEndNotice(null)} />
            <div className="admin-dialog__panel" style={{ maxWidth: 480 }}>
              <div className="admin-dialog__header">
                <h3>{sessionEndNotice.title}</h3>
              </div>
              <div className="admin-dialog__body">
                <p className="admin-muted" style={{ whiteSpace: 'pre-line' }}>{sessionEndNotice.message}</p>
              </div>
              <div className="admin-dialog__footer">
                <button
                  type="button"
                  className="admin-button is-primary"
                  onClick={() => setSessionEndNotice(null)}
                >
                  OK
                </button>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    );
  }

  // TOTP Setup step
  if (step === 'totp_setup') {
    return (
      <div className="admin-login-container admin-login-container--totp">
        <div className="admin-login-shell admin-login-shell--totp admin-login-shell--setup">
          <section className="admin-login-intro admin-login-intro--totp" aria-label="Authenticator setup guidance">
            <span className="admin-login-intro__badge">Account Security Setup</span>
            <h1>Set Up Authenticator</h1>
            <p>
              Link your authenticator app to complete your admin account protection. This setup is required once and
              helps keep privileged access secure.
            </p>
            <ul className="admin-login-intro__highlights" aria-label="Setup reminders">
              <li>
                <Smartphone size={18} />
                <span>Scan the QR code using Google Authenticator, Microsoft Authenticator, or similar apps.</span>
              </li>
              <li>
                <KeyRound size={18} />
                <span>Save your backup codes before continuing to verification.</span>
              </li>
            </ul>
          </section>

          <section className="admin-login-form-column admin-login-form-column--setup">
            <div className="admin-login-card admin-login-card--totp admin-login-card--wide admin-login-card--totp-setup">
              <div className="admin-login-header">
                <div className="admin-logo-circle">
                  <Smartphone size={32} color="#1f4f85" />
                </div>
                <h1>Set Up Authenticator</h1>
                <p>Scan the QR code with your Authenticator App</p>
              </div>

              <div className="admin-login-form">
                {error && <div className="admin-alert is-error">{error}</div>}

                <div className="admin-totp-setup admin-totp-setup--horizontal">
                  <div className="admin-totp-setup__left">
                    <div className="admin-totp-qr">
                      {qrCodeUrl && <img src={qrCodeUrl} alt="TOTP QR Code" />}
                    </div>

                    <div className="admin-totp-manual">
                      <p className="admin-totp-manual__label">Can't scan? Enter this key manually:</p>
                      <div className="admin-totp-manual__secret">
                        <code>{totpSecret}</code>
                        <button
                          type="button"
                          className="admin-totp-copy-btn"
                          onClick={() => copyToClipboard(totpSecret, setCopiedSecret)}
                          title="Copy secret"
                        >
                          {copiedSecret ? <CheckCircle size={16} /> : <Copy size={16} />}
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="admin-totp-setup__right">
                    <div className="admin-totp-backup-section admin-totp-backup-section--setup">
                      <div className="admin-totp-backup-header">
                        <AlertTriangle size={16} />
                        <strong>Backup Codes</strong>
                      </div>
                      <p className="admin-totp-backup-desc">
                        Save these codes securely. Each can be used once if you lose access to your authenticator.
                      </p>
                      <div className="admin-backup-codes">
                        {backupCodes.map((code, i) => (
                          <code key={i} className="admin-backup-code">{code}</code>
                        ))}
                      </div>
                      <button
                        type="button"
                        className="admin-button is-soft is-full-width admin-button--mt-sm"
                        onClick={() => copyToClipboard(backupCodes.join('\n'), setCopiedCodes)}
                      >
                        {copiedCodes ? <><CheckCircle size={16} /> Copied!</> : <><Copy size={16} /> Copy All Codes</>}
                      </button>
                    </div>

                    <label className="admin-totp-checkbox">
                      <input
                        type="checkbox"
                        checked={savedCodes}
                        onChange={(e) => setSavedCodes(e.target.checked)}
                      />
                      <span>I have saved my backup codes in a secure location</span>
                    </label>

                    <button
                      type="button"
                      className="admin-button is-primary is-full-width"
                      disabled={!savedCodes || loading}
                      onClick={() => {
                        if (!savedCodes || loading) return;
                        setTotpCode('');
                        setError(null);
                        setStep('totp_setup_verify');
                      }}
                    >
                      Continue to Verification
                    </button>

                    <button
                      type="button"
                      className="admin-button is-soft is-full-width"
                      onClick={handleBackToLogin}
                    >
                      Back to Login Credentials
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>

        <footer className="admin-login-page-footer">
          <p>Need help? Contact your union representative</p>
          <p>Labor Connect - Luzon regional</p>
        </footer>
      </div>
    );
  }

  // TOTP Setup Verify step
  if (step === 'totp_setup_verify') {
    return (
      <div className="admin-login-container admin-login-container--totp">
        <div className="admin-login-shell admin-login-shell--totp">
          <section className="admin-login-intro admin-login-intro--totp" aria-label="Authenticator verification guidance">
            <span className="admin-login-intro__badge">Final Setup Step</span>
            <h1>Verify Setup</h1>
            <p>
              Confirm your authenticator connection by entering a fresh 6-digit code from your app. After this step,
              you can securely continue to the admin portal.
            </p>
            <ul className="admin-login-intro__highlights" aria-label="Verification reminders">
              <li>
                <KeyRound size={18} />
                <span>Use the latest 6-digit code generated by your authenticator app.</span>
              </li>
              <li>
                <ShieldCheck size={18} />
                <span>This confirms your device is linked and ready for future sign-ins.</span>
              </li>
            </ul>
          </section>

          <section className="admin-login-form-column">
            <div className="admin-login-card admin-login-card--totp">
              <div className="admin-login-header">
                <div className="admin-logo-circle">
                  <KeyRound size={32} color="#1f4f85" />
                </div>
                <h1>Verify Setup</h1>
                <p>Enter the 6-digit code from your authenticator app to complete setup</p>
              </div>

              <form onSubmit={handleVerifySetup} className="admin-login-form">
                {error && <div className="admin-alert is-error">{error}</div>}

                <div className="admin-form-group">
                  <label htmlFor="totp-code">Verification Code</label>
                  <div className="admin-totp-input">
                    <input
                      ref={totpInputRef}
                      type="text"
                      id="totp-code"
                      value={totpCode}
                      onChange={handleTotpCodeChange}
                      placeholder="000000"
                      maxLength={6}
                      autoComplete="one-time-code"
                      inputMode="numeric"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  className="admin-button is-primary is-full-width"
                  disabled={loading || totpCode.length !== 6}
                >
                  {loading ? <Loader2 size={18} className="admin-spin" /> : 'Verify & Complete Setup'}
                </button>

                <button
                  type="button"
                  className="admin-button is-soft is-full-width admin-button--mt-sm"
                  onClick={() => {
                    setTotpCode('');
                    setError(null);
                    setStep('totp_setup');
                  }}
                >
                  Back to QR Code
                </button>
              </form>
            </div>
          </section>
        </div>

        <footer className="admin-login-page-footer">
          <p>Need help? Contact your union representative</p>
          <p>Labor Connect - Luzon regional</p>
        </footer>
      </div>
    );
  }

  // TOTP Verify step (returning admin)
  if (step === 'totp_verify') {
    return (
      <div className="admin-login-container admin-login-container--totp">
        <div className="admin-login-shell admin-login-shell--totp">
          <section className="admin-login-intro admin-login-intro--totp" aria-label="Two-factor security guidance">
            <span className="admin-login-intro__badge">Second Layer Verification</span>
            <h1>Two-Factor Authentication</h1>
            <p>
              Complete one more verification step to continue to the admin portal. This extra layer keeps privileged
              actions protected even if passwords are exposed.
            </p>
            <ul className="admin-login-intro__highlights" aria-label="Two-factor reminders">
              <li>
                <ShieldCheck size={18} />
                <span>Use your authenticator app for a fresh 6-digit code.</span>
              </li>
              <li>
                <KeyRound size={18} />
                <span>Backup codes can be used once each when your device is unavailable.</span>
              </li>
            </ul>
          </section>

          <section className="admin-login-form-column">
            <div className="admin-login-card admin-login-card--totp">
              <div className="admin-login-header">
                <div className="admin-logo-circle">
                  <KeyRound size={30} color="#1f4f85" />
                </div>
                <h1>Two-Factor Authentication</h1>
                <p>
                  {isBackupCode
                    ? 'Enter one of your backup codes'
                    : 'Enter the 6-digit code from your authenticator app'}
                </p>
              </div>

              <form onSubmit={handleVerifyTotp} className="admin-login-form">
                {error && <div className="admin-alert is-error">{error}</div>}

                <div className="admin-form-group">
                  <label htmlFor="totp-code">{isBackupCode ? 'Backup Code' : 'Verification Code'}</label>
                  <div className="admin-totp-input">
                    <input
                      ref={totpInputRef}
                      type="text"
                      id="totp-code"
                      value={totpCode}
                      onChange={handleTotpCodeChange}
                      placeholder={isBackupCode ? 'Enter backup code' : '000000'}
                      maxLength={isBackupCode ? 8 : 6}
                      autoComplete="one-time-code"
                      inputMode={isBackupCode ? 'text' : 'numeric'}
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  className="admin-button is-primary is-full-width"
                  disabled={loading || (isBackupCode ? totpCode.length < 1 : totpCode.length !== 6)}
                >
                  {loading ? <Loader2 size={18} className="admin-spin" /> : 'Verify'}
                </button>

                <div className="admin-totp-toggle">
                  <button
                    type="button"
                    onClick={() => {
                      setIsBackupCode(!isBackupCode);
                      setTotpCode('');
                      setError(null);
                    }}
                  >
                    {isBackupCode ? 'Use authenticator code instead' : 'Use a backup code instead'}
                  </button>
                </div>

                <button type="button" className="admin-button is-soft is-full-width" onClick={handleBackToLogin}>
                  Back to Login
                </button>
              </form>
            </div>
          </section>
        </div>

        <footer className="admin-login-page-footer">
          <p>Need help? Contact your union representative</p>
          <p>Labor Connect - Luzon regional</p>
        </footer>
      </div>
    );
  }

  return null;
}
